import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { RotateCcw, Check, PenTool } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SignaturePadDialog — reusable HTML5 canvas signature pad.
 *
 * Lets the user draw their signature with a finger / stylus / mouse and
 * returns a PNG data URL on save.  Used by FormFillPage and the e-Signature
 * external-signer page.
 *
 * Props:
 *   open          — whether the dialog is open
 *   onClose       — close handler (Cancel or X)
 *   onSave(dataUrl) — called with a PNG data URL when the user clicks Save
 *   label?        — caption shown above the pad ("Patient signature", etc.)
 *   initialValue? — existing dataURL to redraw (lets the user touch up)
 */
export default function SignaturePadDialog({ open, onClose, onSave, label = 'Signature', initialValue = null }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [hasInk, setHasInk] = useState(!!initialValue);

  // (Re)initialise the canvas any time the dialog opens
  useEffect(() => {
    if (!open) return;
    // Defer to next tick so the canvas has been laid out.
    const id = window.setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#0A2342';
      ctx.clearRect(0, 0, rect.width, rect.height);
      if (initialValue) {
        const img = new Image();
        img.onload = () => { try { ctx.drawImage(img, 0, 0, rect.width, rect.height); } catch { /* ignore */ } };
        img.src = initialValue;
        setHasInk(true);
      } else {
        setHasInk(false);
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [open, initialValue]);

  const pt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pt(e);
  };

  const move = useCallback((e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pt(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  }, [hasInk]);

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    const r = c.getBoundingClientRect();
    ctx.clearRect(0, 0, r.width, r.height);
    setHasInk(false);
  };

  const handleSave = () => {
    if (!hasInk) { toast.error('Draw your signature first'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="signature-pad-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2342]">
            <PenTool className="w-5 h-5 text-[#0E7490]" />
            Sign: {label}
          </DialogTitle>
          <p className="text-xs text-[#64748B]">Draw with your finger, stylus, or mouse. The image is captured as PNG.</p>
        </DialogHeader>

        <div
          className="border-2 border-dashed border-[#CBD5E1] rounded-md bg-[#FAFBFC] mt-2"
          style={{ height: '220px' }}
        >
          <canvas
            ref={canvasRef}
            data-testid="signature-pad-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair', borderRadius: '0.375rem' }}
          />
        </div>

        <DialogFooter className="gap-2 flex-wrap mt-4">
          <Button variant="outline" onClick={clear} data-testid="signature-pad-clear-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Clear
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} data-testid="signature-pad-cancel-btn">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!hasInk}
            className="careiq-btn-primary"
            data-testid="signature-pad-save-btn"
          >
            <Check className="w-4 h-4 mr-1" /> Save signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
